import { useState, useEffect } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $recipientListStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { SortType } from '@shared/api';
import { RecipientsTable } from './recipientsTable';
import { Button, Icon, SearchInput, Text } from '@shared/widgets';
import PlusIcon from '@assets/icons/plus.svg';

interface RecipientsProps {
  className?: string;
}

export const Recipients = (props: RecipientsProps) => {
  const { navigate } = useRouteHook();

  const recipientListStore = useUnit($recipientListStore.store);

  const [search, setSearch] = useState<string>('');
  const recipientsState = useListState({ list: 'recipients', count: 5 });

  const { t } = useTranslation('payments');

  useEffect(() => {
    return () => $recipientListStore.events.clear();
  }, []);

  useEffect(() => {
    $recipientListStore.events.getRecipients({
      Count: recipientsState.count,
      FromIndex: recipientsState.fromIndex,
      SearchQuery: search,
      ReturnTotalCount: true,
      SortType: SortType.Asc,
    });
  }, [search, recipientsState]);

  return (
    <section
      className={cn(
        'flex flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <div>
        {PaletteUtils.isInsigniaPalette && (
          <Text weight='semibold' size='2xl'>
            {t('recipients.title')}
          </Text>
        )}
        <div
          className={cn(
            'flex flex-wrap items-center',
            'max-md:gap-6',
            PaletteUtils.isInsigniaPalette && 'justify-end',
            PaletteUtils.isVcfcPalette && 'justify-between',
          )}
        >
          {PaletteUtils.isVcfcPalette && (
            <Text weight='semibold' size='lg'>
              {t('recipients.title')}
            </Text>
          )}
          <div className={cn('flex flex-wrap gap-3', PaletteUtils.isVcfcPalette && 'items-center')}>
            <SearchInput className='shrink-0' withDebounce maxLength={128} value={search} onChange={setSearch} />
            <Button color='outlined' to={RoutePaths.paymentNewRecipients.onNavigate()}>
              <Icon icon={<PlusIcon />} color='inherit' />
              {t('recipients.buttons.addNewRecipient')}
            </Button>
          </div>
        </div>
      </div>
      <RecipientsTable
        isLoading={recipientListStore?.loading}
        error={recipientListStore.error}
        onRefresh={() =>
          $recipientListStore.events.getRecipients({
            Count: recipientsState.count,
            FromIndex: recipientsState.fromIndex,
            SearchQuery: search,
            ReturnTotalCount: true,
            SortType: SortType.Asc,
          })
        }
        data={recipientListStore.data.data}
        totalItemCount={recipientListStore.data.total}
        currentPage={recipientsState.page}
        countItemPage={recipientsState.count}
        onChangePage={recipientsState.onChangePage}
        onChangeCountItemPage={recipientsState.onChangeCount}
        onSelectedItemId={(id) => navigate(RoutePaths.paymentsRecipient.onNavigate(id))}
      />
    </section>
  );
};
